import { inject } from "aurelia";
import { newInstanceForScope } from '@aurelia/kernel';
import { IValidationController } from '@aurelia/validation-html';
import { ICustomElementController } from '@aurelia/runtime-html';
import { IValidationRules } from '@aurelia/validation';
import { IZeusClient, ZeusClient } from '@services/graphql-service/zeus-client';
import { IDialogCustomElementViewModel, IDialogController, DefaultDialogDom, IDialogDom } from "@aurelia/dialog";
import { IAuthService } from "@interfaces/auth-service/auth-service-interface";
import { IFormInputOption } from "@components/form/interfaces/form-interface";
import { IRouter } from "@aurelia/router";
import { digitalMarketingOptions, hearAboutCopymagicoOptions, mainObjectiveOptions } from "./data";
import { nhost } from "@lib/nhost";
import { userOnboarding_constraint, userOnboarding_update_column } from "@zeus";

@inject(IZeusClient, IDialogDom, IValidationRules, newInstanceForScope(IValidationController), IAuthService, IRouter)
export class Onboarding implements IDialogCustomElementViewModel {
  $dialog: IDialogController;
  $controller?: ICustomElementController<this>;

  currentStepIndex: number = 0;

  private digitalMarketingOptions: IFormInputOption[] = digitalMarketingOptions
  private mainObjectiveOptions: IFormInputOption[] = mainObjectiveOptions
  private hearAboutCopymagicoOptions: IFormInputOption[] = hearAboutCopymagicoOptions

  private userName: string
  private digitalMarketingSelecetedOption: string
  private mainObjectiveSelectedOption: string
  private mainObjectiveOther: string
  private hearAboutCopymagicoSelectedOption: string
  private selectedAvatarUrl: string

  private avatarImages: string[] = [
    '359cc479-33e0-4959-8c85-df7af95cc670',
    'fd2ff20c-a13f-4ad1-8ad0-0a220cba89a2',
    '4a3cf09a-7a06-488a-83bf-147e4790ae46',
    '7d22993e-b11a-4d5f-a309-152d814945e5',
    '250494c8-4542-4414-904b-76d7477d7377',
    'ce06d5e5-e1c1-4cfb-a911-04c805f092e2',
    '41ec1cbe-2d0f-4666-8c22-8a08675d197a',
    'f6cdce7c-f99d-4b93-a805-e6c9bf9de583',
    '485b9bdc-2043-4c84-8262-98a84e015cdb',
  ]

  constructor(
    private zeusClient: ZeusClient,
    private dialogDom: DefaultDialogDom,
    private validationRules: IValidationRules,
    private validationController: IValidationController,
    private auth: IAuthService,
    private router: IRouter,
  ) {
    validationRules.on(this)
      .ensure('userName').required().withMessage('Precisamos dessa informação.')
      .ensure('digitalMarketingSelecetedOption').required().withMessage('Você precisa escolher uma opção.')
      .ensure('mainObjectiveSelectedOption').required().withMessage('Precisamos dessa informação.')
      .ensure('mainObjectiveOther').required().withMessage('Precisamos dessa informação.').when(() => this.mainObjectiveSelectedOption == 'Outro')
      .ensure('hearAboutCopymagicoSelectedOption').required().withMessage('Precisamos dessa informação.')
      .ensure('selectedAvatarUrl').required().withMessage('Precisamos dessa informação.')
  }

  private signOut = async () => {
    try {
      await this.auth.signOut();
      this.$dialog.cancel()
      this.router.load('login');

    } catch (error) {
      console.log(error);
    }
  }

  async getImages() {
    this.avatarImages = this.avatarImages.map((a) => {
      return nhost.storage.getPublicUrl({ fileId: a });
    })
  }

  async loadOnboardingData() {

    const response = await this.zeusClient.chain('query')({
      user: [
        {
          id: this.auth.getUser().id
        },
        {
          displayName: true,
          avatarUrl: true,
          email: true,
        }
      ],
      userOnboarding_by_pk: [
        { userId: this.auth.getUser().id },
        {
          currentStep: true,
          digitalMarketingExperience: true,
          mainObjective: true,
          mainObjectiveOther: true,
          hearAboutCopymagico: true,
          done: true
        }
      ]
    })

    this.userName = response?.user?.displayName == response?.user?.email ? '' : response?.user?.displayName
    this.digitalMarketingSelecetedOption = response?.userOnboarding_by_pk?.digitalMarketingExperience
    this.mainObjectiveSelectedOption = response?.userOnboarding_by_pk?.mainObjective
    this.mainObjectiveOther = response?.userOnboarding_by_pk?.mainObjectiveOther
    this.hearAboutCopymagicoSelectedOption = response?.userOnboarding_by_pk?.hearAboutCopymagico
    this.selectedAvatarUrl = this.avatarImages.find((a) => a == response?.user?.avatarUrl) ? response?.user?.avatarUrl : this.avatarImages[0]

    this.currentStepIndex = response?.userOnboarding_by_pk?.currentStep || 0
  }

  selectImage(avatarImage: string) {
    this.selectedAvatarUrl = avatarImage;
  }

  async activate() {
    await this.getImages();
    await this.loadOnboardingData();
  }

  async saveStep(): Promise<void> {
    if (this.currentStepIndex === 0) {
      const { valid } = await this.validationController.validate();

      if (!valid) return;

      await this.zeusClient.chain('mutation')({
        updateUser: [
          {
            pk_columns: { id: this.auth.getUser().id },
            _set: {
              displayName: this.userName,
            }
          },
          {
            id: true
          }
        ],
        insert_userOnboarding_one: [
          {
            object: {
              userId: this.auth.getUser().id,
              currentStep: 1,
              digitalMarketingExperience: this.digitalMarketingSelecetedOption,
              mainObjective: this.mainObjectiveSelectedOption,
              mainObjectiveOther: this.mainObjectiveOther,
              hearAboutCopymagico: this.hearAboutCopymagicoSelectedOption,
              done: false,
            },
            on_conflict: {
              constraint: userOnboarding_constraint.userOnboarding_pkey,
              update_columns: [
                userOnboarding_update_column.currentStep,
                userOnboarding_update_column.digitalMarketingExperience,
                userOnboarding_update_column.mainObjective,
                userOnboarding_update_column.mainObjectiveOther,
                userOnboarding_update_column.hearAboutCopymagico,
                userOnboarding_update_column.done,
              ]
            }
          },
          {
            currentStep: true,
          }
        ],
      })

    } else if (this.currentStepIndex === 1) {

      await this.zeusClient.chain('mutation')({
        updateUser: [
          {
            pk_columns: { id: this.auth.getUser().id },
            _set: {
              avatarUrl: this.selectedAvatarUrl,
            }
          },
          {
            id: true
          }
        ],
        insert_userOnboarding_one: [
          {
            object: {
              userId: this.auth.getUser().id,
              currentStep: 2,
              done: false,
            },
            on_conflict: {
              constraint: userOnboarding_constraint.userOnboarding_pkey,
              update_columns: [
                userOnboarding_update_column.currentStep,
                userOnboarding_update_column.done,
              ]
            }
          },
          {
            currentStep: true,
          }
        ],
      })

    } else if (this.currentStepIndex === 2) {

      await this.zeusClient.finishOnBoard(false)

      await this.$dialog.ok()

    }

    this.currentStepIndex++

  }

  previousStep() {
    this.currentStepIndex--;
  }
}